import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
    getAllTickets,
    createNewTicket,
    addTicketFollowup,
    closeTicket,
    getTicketsReport
} from "../services/api";
import {
    Ticket,
    Plus,
    MessageSquare,
    Clock,
    AlertCircle,
    BarChart3,
    CheckCircle2,
    Clock3,

    ChevronRight,
    ChevronLeft,
    Send,
    Loader2,
    ArrowLeft,
    XCircle,
    FileText,
} from "lucide-react";
import Skeleton from "../Components/SkeletonLoader";
import useAuth from "../useAuth";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default function TicketTrackingPage() {
    const { user } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRaisingTicket, setIsRaisingTicket] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [newTicketData, setNewTicketData] = useState({ deviceName: "", subject: "", description: "" });
    const [followupMessage, setFollowupMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [stats, setStats] = useState({ total: 0, open: 0, closed: 0 });
    const [statusFilter, setStatusFilter] = useState("all");
    const [viewMode, setViewMode] = useState("list"); // 'list' or 'reports'
    const [reportData, setReportData] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Note: Assuming a GET API exists for tickets. If not, this might need adjustment.
    const fetchTickets = async () => {
        setLoading(true);
        try {
            const userWise = user?.userType === "Others" ? "true" : "false";
            const ticketsRes = await getAllTickets(userWise);

            if (ticketsRes.success) {
                // API returns summary fields and an array under `data`.
                const fetchedTickets = ticketsRes.data || ticketsRes.data?.data || [];
                setTickets(fetchedTickets);

                // Prefer API-provided counts when available for accuracy/pagination
                const calculatedStats = {
                    total: typeof ticketsRes.count === 'number' ? ticketsRes.count : fetchedTickets.length,
                    open: typeof ticketsRes.openTickets === 'number' ? ticketsRes.openTickets : fetchedTickets.filter(t => t.status?.toLowerCase() === "open").length,
                    closed: typeof ticketsRes.closedTickets === 'number' ? ticketsRes.closedTickets : fetchedTickets.filter(t => t.status?.toLowerCase() === "closed").length,
                };
                setStats(calculatedStats);
            }
        } catch (error) {
            console.error("Error fetching tickets:", error);
            setTickets([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchTickets();
        }
    }, [user]);

    const handleRaiseTicket = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await createNewTicket(newTicketData);
            if (response.success) {
                toast.success("Ticket raised successfully!");
                setNewTicketData({ deviceName: "", subject: "", description: "" });
                setIsRaisingTicket(false);
                fetchTickets();
            }
        } catch (error) {
            toast.error(error.message || "Failed to raise ticket");
        } finally {
            setSubmitting(false);
        }
    };

    const handleFollowup = async (e) => {
        e.preventDefault();
        if (!followupMessage.trim()) return;
        setSubmitting(true);
        try {
            const response = await addTicketFollowup(selectedTicket.id, followupMessage);
            if (response.success) {
                toast.success("Followup sent!");
                setFollowupMessage("");

                // Use the returned followup data from the API response
                const newFollowup = response.data.followup;

                // Update local state accurately
                setSelectedTicket(prev => ({
                    ...prev,
                    status: response.data.status || prev.status,
                    followups: [...(prev.followups || []), newFollowup]
                }));

                // Also refresh the overall list to sync status
                fetchTickets();
            }
        } catch (error) {
            toast.error(error.message || "Failed to send followup");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseTicket = async (ticket) => {
        const targetTicket = ticket?.id ? ticket : selectedTicket;
        if (!targetTicket) return;

        if (!window.confirm("Are you sure you want to close this ticket?")) return;
        setSubmitting(true);
        try {
            const response = await closeTicket(targetTicket.id);
            if (response.success) {
                toast.success(response.message || "Ticket closed successfully!");
                fetchTickets();
                if (selectedTicket?.id === targetTicket.id) {
                    setSelectedTicket(prev => ({ ...prev, status: "closed" }));
                }
            }
        } catch (error) {
            toast.error(error.message || "Failed to close ticket");
        } finally {
            setSubmitting(false);
        }
    };

    const fetchReport = async () => {
        setReportLoading(true);
        try {
            const userWise = user?.userType === "Others" ? "true" : "false";
            const response = await getTicketsReport(userWise);
            if (response.success) {
                // Accept multiple possible shapes returned by the report API
                const dataFromResp = response.data?.tickets || response.data?.data || response.data || [];
                setReportData(Array.isArray(dataFromResp) ? dataFromResp : []);
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            toast.error("Failed to load report data");
        } finally {
            setReportLoading(false);
        }
    };

    useEffect(() => {
        if (viewMode === "reports") {
            fetchReport();
        }
    }, [viewMode, user]);

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.text("Ticket Reports", 14, 15);
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

        const tableData = reportData.map(ticket => [
            ticket.deviceName || "N/A",
            ticket.subject,
            new Date(ticket.createdAt).toLocaleString(),
            ticket.closedAt ? new Date(ticket.closedAt).toLocaleString() : "N/A",
            ticket.resolutionTimeMinutes?.toFixed(2) || "0.00",
            ticket.totalFollowups || 0,
            ticket.lastFollowupMessage || "—"
        ]);

        autoTable(doc, {
            head: [['Device', 'Subject', 'Created At', 'Closed At', 'Res. Time (Min)', 'Updates', 'Last Remark']],
            body: tableData,
            startY: 28,
            theme: 'grid',
            headStyles: { fillColor: [37, 99, 235], fontSize: 10, cellPadding: 3 },
            bodyStyles: { fontSize: 9, cellPadding: 3 },
            alternateRowStyles: { fillColor: [245, 247, 250] }
        });

        doc.save(`ticket_report_${new Date().getTime()}.pdf`);
    };

    const handleExportExcel = () => {
        const headers = ["Device Name", "Subject", "Created At", "Closed At", "Resolution Time (Min)", "Total Followups", "Last Remark"];
        const csvContent = [
            headers.join(","),
            ...reportData.map(ticket => [
                `"${ticket.deviceName || 'N/A'}"`,
                `"${ticket.subject.replace(/"/g, '""')}"`,
                `"${new Date(ticket.createdAt).toLocaleString()}"`,
                `"${ticket.closedAt ? new Date(ticket.closedAt).toLocaleString() : 'N/A'}"`,
                ticket.resolutionTimeMinutes?.toFixed(2) || "0.00",
                ticket.totalFollowups || 0,
                `"${(ticket.lastFollowupMessage || "").replace(/"/g, '""')}"`
            ].join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `ticket_report_${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const statusColors = {
        open: "bg-blue-50 text-blue-700 border-blue-200",
        closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
        pending: "bg-amber-50 text-amber-700 border-amber-200",
    };

    const filteredTickets = tickets.filter(t => {
        if (statusFilter === "all") return true;
        return t.status?.toLowerCase() === statusFilter.toLowerCase();
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedTickets = filteredTickets.slice(startIndex, endIndex);

    // Reset to first page when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    if (loading && tickets.length === 0) {
        return (
            <div className="p-8 max-w-5xl mx-auto space-y-6">
                <Skeleton className="h-10 w-48" />
                <div className="grid grid-cols-1 gap-4">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-24 w-full rounded-xl" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Support Tickets</h1>
                    <p className="text-sm text-gray-500 mt-1">Raise and track your complaints or queries.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex bg-gray-100 p-1 rounded-xl">
                        <button
                            onClick={() => setViewMode("list")}
                            className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            List View
                        </button>
                        <button
                            onClick={() => setViewMode("reports")}
                            className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${viewMode === 'reports' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            Reports
                        </button>
                    </div>
                    {!isRaisingTicket && !selectedTicket && viewMode === "list" && (
                        <button
                            onClick={() => setIsRaisingTicket(true)}
                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
                        >
                            <Plus className="h-5 w-5" />
                            Raise New Ticket
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Summary */}
            {!isRaisingTicket && !selectedTicket && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-in fade-in slide-in-from-top-4 duration-500">
                    <button
                        onClick={() => setStatusFilter("all")}
                        className={`bg-white border ${statusFilter === 'all' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'} p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:shadow-md transition-all text-left outline-none`}
                    >
                        <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                            <BarChart3 className="h-6 w-6" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Tickets</p>
                            <p className="text-2xl font-black text-gray-900">{stats.total || 0}</p>
                        </div>
                    </button>
                    <button
                        onClick={() => setStatusFilter("open")}
                        className={`bg-white border ${statusFilter === 'open' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'} p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:shadow-md transition-all text-left outline-none`}
                    >
                        <div className="h-12 w-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                            <Clock3 className="h-6 w-6" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Open Tickets</p>
                            <p className="text-2xl font-black text-gray-900">{stats.open || 0}</p>
                        </div>
                    </button>
                    <button
                        onClick={() => setStatusFilter("closed")}
                        className={`bg-white border ${statusFilter === 'closed' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'} p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:shadow-md transition-all text-left outline-none`}
                    >
                        <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                            <CheckCircle2 className="h-6 w-6" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Closed Tickets</p>
                            <p className="text-2xl font-black text-gray-900">{stats.closed || 0}</p>
                        </div>
                    </button>
                </div>
            )}

            {isRaisingTicket ? (
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-lg font-bold text-gray-900">Raise New Ticket</h2>
                        <button
                            onClick={() => setIsRaisingTicket(false)}
                            className="text-gray-400 hover:text-gray-600 p-2"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                    </div>
                    <form onSubmit={handleRaiseTicket} className="p-6 space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Device Name</label>
                            <input
                                type="text"
                                required
                                value={newTicketData.deviceName}
                                onChange={(e) => setNewTicketData({ ...newTicketData, deviceName: e.target.value })}
                                placeholder="e.g. PC-101, Mobile, Printer"
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Subject</label>
                            <input
                                type="text"
                                required
                                value={newTicketData.subject}
                                onChange={(e) => setNewTicketData({ ...newTicketData, subject: e.target.value })}
                                placeholder="Brief summary of the issue"
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Description</label>
                            <textarea
                                required
                                rows={4}
                                value={newTicketData.description}
                                onChange={(e) => setNewTicketData({ ...newTicketData, description: e.target.value })}
                                placeholder="Describe your problem in detail..."
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all resize-none"
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                                Submit Ticket
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsRaisingTicket(false)}
                                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-bold transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            ) : selectedTicket ? (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                    <button
                        onClick={() => setSelectedTicket(null)}
                        className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-blue-600 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to List
                    </button>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-6">
                            {/* Ticket Content */}
                            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
                                <div className="flex items-start justify-between">
                                    <h2 className="text-xl font-bold text-gray-900">{selectedTicket.subject}</h2>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${statusColors[selectedTicket.status] || "bg-gray-100"}`}>
                                        {selectedTicket.status?.toUpperCase()}
                                    </span>
                                </div>
                                {selectedTicket.deviceName && (
                                    <div className="flex items-center gap-2 text-sm text-blue-600 font-bold bg-blue-50 w-fit px-3 py-1 rounded-lg">
                                        Device: {selectedTicket.deviceName}
                                    </div>
                                )}
                                <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                                    <p className="text-gray-600 leading-relaxed whitespace-pre-wrap flex-1">{selectedTicket.description}</p>
                                    {selectedTicket.status?.toLowerCase() !== "closed" && (

                                        <button
                                            onClick={handleCloseTicket}
                                            disabled={submitting}
                                            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-100 rounded-lg text-xs font-bold transition-all shadow-sm"
                                        >
                                            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                                            Close Ticket
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-400 font-medium pt-4 border-t border-gray-50">
                                    <div className="flex items-center gap-1.5">
                                        <Clock className="h-3.5 w-3.5" />
                                        {new Date(selectedTicket.createdAt).toLocaleString()}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Ticket className="h-3.5 w-3.5" />
                                        ID: #{selectedTicket.id}
                                    </div>
                                </div>
                            </div>

                            {/* Followups */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider px-2">Conversation</h3>
                                <div className="space-y-4">
                                    {(selectedTicket.followups || []).length === 0 ? (
                                        <div className="text-center py-8 bg-gray-50 rounded-2xl border border-dashed border-gray-200 text-gray-400 text-sm">
                                            No followup messages yet.
                                        </div>
                                    ) : (
                                        selectedTicket.followups.map((f, i) => (
                                            <div key={i} className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 ml-8 relative">
                                                <div className="absolute -left-4 top-6 w-4 h-4 border-l border-b border-blue-100 rounded-bl-lg" />
                                                <p className="text-sm text-gray-700 leading-relaxed">{f.message}</p>
                                                <div className="text-[10px] text-blue-400 font-bold mt-2 uppercase tracking-tighter">
                                                    {new Date(f.createdAt).toLocaleString()}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* New Followup Form */}
                                {selectedTicket.status !== "closed" && (
                                    <form onSubmit={handleFollowup} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex gap-3">
                                        <input
                                            type="text"
                                            value={followupMessage}
                                            onChange={(e) => setFollowupMessage(e.target.value)}
                                            placeholder="Type your message here..."
                                            className="flex-1 bg-gray-50 border-none rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                        />
                                        <button
                                            type="submit"
                                            disabled={submitting || !followupMessage.trim()}
                                            className="bg-blue-600 hover:bg-blue-700 text-white p-2.5 rounded-xl transition-all disabled:opacity-50"
                                        >
                                            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>

                        {/* Sidebar info */}
                        <div className="space-y-6">
                            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
                                <h3 className="text-sm font-bold text-gray-900">User Information</h3>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                            <AlertCircle className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-400 font-bold uppercase tracking-tighter">Created By</div>
                                            <div className="text-sm font-medium text-gray-700">{selectedTicket.createdByUser?.fullName || "User"}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                            <Clock className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-400 font-bold uppercase tracking-tighter">Department</div>
                                            <div className="text-sm font-medium text-gray-700">{selectedTicket.createdByUser?.receivedDepartment || "N/A"}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : viewMode === "reports" ? (
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Tickets Report</h2>
                            <p className="text-xs text-gray-500">View and export detailed ticket data.</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleExportPDF}
                                disabled={reportLoading || reportData.length === 0}
                                className="flex items-center gap-2 bg-rose-50 text-rose-600 hover:bg-rose-100 px-4 py-2 rounded-xl text-sm font-bold transition-all border border-rose-100 disabled:opacity-50"
                            >
                                <FileText className="h-4 w-4" /> Export PDF
                            </button>
                            <button
                                onClick={handleExportExcel}
                                disabled={reportLoading || reportData.length === 0}
                                className="flex items-center gap-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 px-4 py-2 rounded-xl text-sm font-bold transition-all border border-emerald-100 disabled:opacity-50"
                            >
                                <BarChart3 className="h-4 w-4" /> Export Excel
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[800px]">
                            <thead>
                                <tr className="bg-gray-50/50">
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Device</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Subject</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Created At</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Closed At</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Res. Time (Min)</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 text-center">Updates</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Last Remark</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {reportLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <tr key={i}>
                                            <td colSpan={6} className="px-6 py-8"><Skeleton className="h-10 w-full" /></td>
                                        </tr>
                                    ))
                                ) : reportData.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="p-3 bg-gray-50 rounded-full">
                                                    <FileText className="h-8 w-8 text-gray-300" />
                                                </div>
                                                <p className="text-sm text-gray-400 font-medium">No report data available.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    reportData.map((ticket) => (
                                        <tr key={ticket.id} className="hover:bg-gray-50/30 transition-all border-b border-gray-50 last:border-0">
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-bold text-gray-900">{ticket.deviceName || "N/A"}</span>
                                                    <span className="text-[10px] font-bold text-gray-400 font-mono tracking-tighter">#{ticket.id}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-600 font-medium line-clamp-1">{ticket.subject}</span>
                                            </td>
                                            <td className="px-6 py-4 text-[11px] text-gray-500 leading-tight">
                                                {new Date(ticket.createdAt).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-[11px] text-gray-500 leading-tight">
                                                {ticket.closedAt ? new Date(ticket.closedAt).toLocaleString() : <span className="text-blue-500 font-bold uppercase tracking-tight">Open</span>}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm font-mono text-gray-600 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                                                    {ticket.resolutionTimeMinutes?.toFixed(1) || "0.0"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="inline-flex items-center justify-center h-6 min-w-[24px] px-1.5 bg-gray-100 text-gray-600 rounded-md text-[10px] font-bold">
                                                    {ticket.totalFollowups || 0}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="text-xs text-gray-600 font-medium line-clamp-2 max-w-[200px]">
                                                    {ticket.lastFollowupMessage || <span className="text-gray-400 italic">No remarks</span>}
                                                </p>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    {paginatedTickets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-white border border-dashed border-gray-200 rounded-3xl space-y-4">
                            <div className="p-4 bg-gray-50 rounded-full">
                                <Ticket className="h-10 w-10 text-gray-300" />
                            </div>
                            <div className="text-center">
                                <h3 className="font-bold text-gray-900">No {statusFilter !== 'all' ? statusFilter : ''} tickets found</h3>
                                <p className="text-sm text-gray-500">
                                    {statusFilter === 'all'
                                        ? "You haven't raised any tickets yet."
                                        : `There are no tickets with status "${statusFilter}".`}
                                </p>
                            </div>
                            {statusFilter === 'all' && (
                                <button
                                    onClick={() => setIsRaisingTicket(true)}
                                    className="text-blue-600 font-bold text-sm hover:underline"
                                >
                                    Raise your first ticket
                                </button>
                            )}
                            {statusFilter !== 'all' && (
                                <button
                                    onClick={() => setStatusFilter("all")}
                                    className="text-blue-600 font-bold text-sm hover:underline"
                                >
                                    View all tickets
                                </button>
                            )}
                        </div>
                    ) : (
                        paginatedTickets.map((ticket) => (
                            <div
                                key={ticket.id}
                                onClick={() => setSelectedTicket(ticket)}
                                className="group bg-white border border-gray-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer relative overflow-hidden"
                            >
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-transparent group-hover:bg-blue-500 transition-colors" />
                                <div className="flex items-start justify-between gap-4">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-bold text-gray-400 font-mono tracking-tighter">#{ticket.id}</span>
                                            <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-1">{ticket.subject}</h3>
                                            {ticket.deviceName && (
                                                <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-md font-bold">
                                                    {ticket.deviceName}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 line-clamp-1">{ticket.description}</p>
                                        <div className="flex items-center gap-3 pt-2">
                                            <div className="flex items-center gap-1 text-[10px] text-gray-400 font-bold">
                                                <Clock className="h-3 w-3" />
                                                {new Date(ticket.createdAt).toLocaleDateString()}
                                            </div>
                                            <div className="flex items-center gap-1 text-[10px] text-gray-400 font-bold">
                                                <MessageSquare className="h-3 w-3" />
                                                {(ticket.followups || []).length} Updates
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-3 shrink-0">
                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${statusColors[ticket.status?.toLowerCase()] || "bg-gray-100"}`}>
                                            {ticket.status?.toUpperCase()}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            {ticket.status?.toLowerCase() !== "closed" && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCloseTicket(ticket);
                                                    }}
                                                    className="flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-100 rounded-lg text-xs font-bold transition-all shadow-sm"
                                                    title="Close Ticket"
                                                >
                                                    {submitting && (selectedTicket?.id === ticket.id || !selectedTicket) ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    ) : (
                                                        <XCircle className="h-3.5 w-3.5" />
                                                    )}
                                                    Close
                                                </button>
                                            )}
                                            <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-blue-400 transform group-hover:translate-x-1 transition-all" />
                                        </div>

                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination Footer */}
                {filteredTickets.length > 0 && (
                    <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="flex flex-col sm:flex-row items-center gap-4 text-xs font-medium text-gray-500">
                            <span>
                                Showing{" "}
                                <span className="text-gray-900 font-bold">
                                    {paginatedTickets.length}
                                </span>{" "}
                                of <span className="text-gray-900 font-bold">{filteredTickets.length}</span>{" "}
                                tickets
                            </span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 cursor-pointer"
                            >
                                {[5, 10, 20, 50].map((size) => (
                                    <option key={size} value={size}>
                                        {size} per page
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <div className="px-4 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-mono font-medium text-gray-700 shadow-sm">
                                Page {currentPage} of {totalPages || 1}
                            </div>
                            <button
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
                </>
            )}
        </div>
    );
}
